import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { parseProject } from '../core/hp/parseProject';
import { ModelLibrary } from '../render/three/ModelLibrary';
import { fetchCollabSession, fetchSharePreview, setCatalogProject, subscribeWebStates, webStates } from '../state/catalogApi';
import { signOut, webAuthToken } from '../state/webAuth';
import { projectFileRequest } from '../state/projectFileRequest';
import { browserClock, pageVisibility, RtConnection } from '../collaboration/rt/connection';
import { LiveSession } from '../collaboration/rt/liveSession';
import { CatalogShare } from '../collaboration/share/catalogShare';
import { setCatalogTextureBase, setModelAssetBase } from '../render/textures';
import { installCursorVars } from '../render/three/cursors';
import { store, type EditScope, type ViewMode } from '../state/store';
import { buildingShowsIn3D } from '../state/floors';
import { createEmptyScene } from '../core/geometry/edit';
import { isTrashed, writeProject } from '../state/projectLibrary';
import { RECENTLY_DELETED, rememberFolder } from '../state/projectFolders';
import { setAppAccess } from '../agent/editorAccess';
import { copyProjectView, readProjectView, type ProjectView } from '../state/projectView';
import { DEFAULT_WINDOW_VIEW } from '../state/windowViews';
import { CollaborationSession } from '../collaboration/session';
import { createUnavailableTransport } from '../collaboration/unavailable';
import type { CollaborationTransport, SharedSnapshot } from '../collaboration/protocol';
import { collaborationError, rememberShared, sharedDocumentId } from '../collaboration/projectLinks';
import { JoinSharedProject } from './collaboration/JoinSharedProject';
import { SessionAlerts, type InviteFailure } from './collaboration/SessionAlerts';
import { demoProject } from './home/DemoProjects';
import { EditorView, type EditorHandle } from './EditorView';
import { ProjectHome, type HomeSection } from './ProjectHome';
import { useStore } from './useStore';
import { isTextOf, t, type TranslationKey } from '../i18n/I18n';

/**
 * The mode a project opens in, once its floor is in the store: the one it was left in, else 3D, as the app reopens on
 * its saved view and level — an empty floor in 3D over the floors below it (`buildingShowsIn3D`). A floor with nothing
 * drawn on it or under it opens on the plan, never into an empty stage.
 */
const openingMode = (saved: ViewMode | undefined): ViewMode =>
  buildingShowsIn3D(store.get().scene?.levels ?? [], store.get().activeLevelIndex) ? saved ?? 'scene' : 'plan';
// Shared projects wait for the company's collaboration server (docs/server-needs.md). Until it is
// connected the transport refuses every call and the editor keeps sharing out of sight.
const collaborationConfigured: boolean = false;
const noSubscribe = () => () => {};

const base = import.meta.env.VITE_CATALOG_TEXTURE_BASE;
// The site has no /hp/tex and no model tree under /hp/model (deploy/web/server.mjs answers 404): there a picture named
// by such a path keeps its slot's look.
if (base || !import.meta.env.DEV) setCatalogTextureBase(base || null);
if (!import.meta.env.DEV) setModelAssetBase(null);
// A picture of a model's own archive comes out of the model's web GLB
// (`ModelLibrary.archivePicture`): no path on this site serves those pictures.

export function App() {
  const state = useStore(state => ({ sceneOpen: !!state.scene, error: state.error, saveError: state.saveError, projectName: state.projectName }));
  const request = useRef(0);
  const editor = useRef<EditorHandle | null>(null);
  const transport = useRef<CollaborationTransport | null>(null);
  const activeSession = useRef<CollaborationSession | null>(null);
  const openedShared = useRef<string | null>(null);
  const [session, setSession] = useState<CollaborationSession | null>(null);
  const collaborationStatus = useSyncExternalStore(session?.subscribe ?? noSubscribe, () => session?.get().status ?? 'local');
  const activeLive = useRef<{ session: LiveSession; share: CatalogShare; projectId: string; name: string } | null>(null);
  const [live, setLive] = useState<{ session: LiveSession; share: CatalogShare } | null>(null);
  const liveTerminal = useSyncExternalStore(live?.session.subscribe ?? noSubscribe, () => live?.session.get().terminal ?? null);
  const closeLive = useCallback(() => { activeLive.current?.session.close(); activeLive.current?.share.dispose(); activeLive.current = null; setLive(null); }, []);
  const getTransport = useCallback(() => transport.current ??= createUnavailableTransport(), []);
  const [destination, setDestination] = useState(readDestination);
  const [inviteFailure, setInviteFailure] = useState<InviteFailure | null>(null);
  const current = useRef(destination);
  const historyIndex = useRef(typeof history.state?.hpIndex === 'number' ? history.state.hpIndex : 0);
  const open = destination.project;
  useEffect(() => installCursorVars(document.documentElement), []);
  /** `replace`: the entry left behind is overwritten, so Back cannot return to it. */
  const navigate = useCallback((next: Destination, fromHistory = false, replace = false) => {
    const previous = current.current;
    if (previous.project?.id !== next.project?.id || previous.project?.invite !== next.project?.invite) {
      // Leaving is where the project's view and its frame are recorded — before the store
      // drops the scene, and whether the user left by the button or by browser Back.
      editor.current?.captureView();
      if (!store.closeScene()) {
        if (fromHistory) {
          const targetIndex = history.state?.hpIndex;
          if (typeof targetIndex === 'number' && targetIndex !== historyIndex.current) history.go(historyIndex.current - targetIndex);
          else history.replaceState({ hpIndex: historyIndex.current }, '', destinationUrl(previous));
        }
        return;
      }
      request.current += 1;
      setInviteFailure(null);
      activeSession.current?.close(); activeSession.current = null; setSession(null); openedShared.current = null;
      closeLive();
    }
    current.current = next;
    setDestination(next);
    if (fromHistory) historyIndex.current = history.state?.hpIndex ?? historyIndex.current;
    else if (replace) history.replaceState({ hpIndex: historyIndex.current }, '', destinationUrl(next));
    else {
      historyIndex.current += 1;
      history.pushState({ hpIndex: historyIndex.current }, '', destinationUrl(next));
    }
  }, [closeLive]);
  const enterShared = useCallback(async (snapshot: SharedSnapshot, sourceId?: string, inviteToken?: string) => {
    const sourceState = store.get(), id = 'shared-' + snapshot.documentId;
    let clientId = sessionStorage.getItem('hp.collab.clientId');
    if (!clientId) { clientId = crypto.randomUUID(); sessionStorage.setItem('hp.collab.clientId', clientId); }
    if (sourceId) {
      editor.current?.captureView(); store.saveLocal();
      if (store.get().saveError) throw new DOMException('Unable to save the local source', 'QuotaExceededError');
      copyProjectView(sourceId, id);
    }
    rememberShared(snapshot, sourceId, inviteToken);
    const view = readProjectView(id);
    activeSession.current?.close();
    request.current += 1;
    store.loadScene(snapshot.scene, id, snapshot.name, sourceId ? sourceState.activeLevelIndex : view?.levelIndex ?? 0);
    store.set({ mode: sourceId ? sourceState.mode : openingMode(view?.mode),
      ...(sourceId ? { planSurface: sourceState.planSurface, wallView: sourceState.wallView }
        : { planSurface: view?.planSurface ?? 'floor', firstPerson: !!view?.firstPerson, showCeiling: !!view?.showCeiling, wallMode: view?.wallMode ?? 'full' }),
      windowView: view?.windowView ?? DEFAULT_WINDOW_VIEW, windowViewRotation: view?.windowViewRotation ?? 0, showWindowViews: false });
    const shared = new CollaborationSession({ store, transport: getTransport(), storage: localStorage, clientId });
    activeSession.current = shared; openedShared.current = snapshot.documentId; setSession(shared);
    const next: Destination = { section: current.current.section, project: { id, name: snapshot.name, shared: snapshot.documentId } };
    current.current = next; setDestination(next);
    history.replaceState({ ...history.state, hpIndex: historyIndex.current }, '', destinationUrl(next));
    await shared.start(snapshot);
  }, [getTransport]);
  useEffect(() => {
    history.replaceState({ ...history.state, hpIndex: historyIndex.current }, '', destinationUrl(current.current));
    const pop = () => navigate(readDestination(), true);
    window.addEventListener('popstate', pop);
    return () => window.removeEventListener('popstate', pop);
  }, [navigate]);

  // The interface reads the queue's answers from the store; one subscription keeps it current.
  useEffect(() => subscribeWebStates(() => store.set({ modelStates: Object.fromEntries(webStates()) })), []);

  const load = useCallback(async (id: string, name: string, shared?: string) => {
    const token = ++request.current;
    const view = readProjectView(id);
    startLoading(id, name, view);
    try {
      if (shared) {
        if (!collaborationConfigured) throw new Error('collaboration_unavailable');
        const snapshot = await getTransport().read(shared);
        if (token !== request.current) return;
        if (snapshot.documentId !== shared) throw Object.assign(new Error('Invalid project'), { code: 'invalid' });
        await enterShared(snapshot);
        return;
      }
      // A project in «Недавно удалённые» opens once it is restored, as the app's trash offers nothing else; going back
      // to the projects lands on it there. A link or browser Back is how one gets here.
      if (isTrashed(id)) {
        rememberFolder(RECENTLY_DELETED);
        store.set({ loading: false, error: t('#AppProjectInTrash') });
        return;
      }
      // Editing is local-first: a refresh should restore the last intentional change
      // immediately, while the remote XML remains the source for a new project.
      const local = store.restoreLocal(id);
      if (local) {
        store.loadScene(local.scene, id, local.name || name, view?.levelIndex ?? 0);
        store.set({ mode: openingMode(view?.mode) });
        return;
      }
      if (id.startsWith('local-')) throw new Error(t('#AppLocalProjectNotFound'));
      await loadCatalogProject(id, name, view, () => token !== request.current);
    } catch (e) {
      if (token !== request.current) return;
      store.set({ loading: false, error: shared ? !collaborationConfigured ? t('#AppCollaborationUnavailable') : collaborationError(e) : t('#AppOpenFailed', id, String(e)) });
    }
  }, [enterShared, getTransport]);

  // A guest sees the catalog's copy; a signed-in person joins the app's live room, where the copy is the room's own.
  const openGuest = useCallback(async (projectId: string, name: string, stale: () => boolean) => {
    try {
      await loadCatalogProject(projectId, name, null, stale, { edit: 'none' });
    } catch (error) {
      if (stale()) return;
      store.set({ loading: false });
      setInviteFailure(mayPassOnRetry(error) ? 'unreachable' : 'unreadable');
    }
  }, []);

  const openLive = useCallback((projectId: string, name: string, linkToken: string) => {
    closeLive();
    const library = new ModelLibrary();
    const connection = new RtConnection({
      fetchSession: async signal => {
        const token = webAuthToken();
        return token ? fetchCollabSession(projectId, token, linkToken, signal) : { kind: 'signed-out' };
      },
      openSocket: url => new WebSocket(url),
      clock: browserClock,
      visibility: pageVisibility,
    });
    const shared = new LiveSession({
      store, projectId, name, connection, clock: browserClock,
      parse: xml => parseProject(xml, { ...catalogParseOptions(library, projectId), stableIds: true }),
      schedule: run => { requestAnimationFrame(() => run()); },
      prepareModels: async models => {
        await library.manifest();
        setCatalogProject(projectId);
        await library.prepare(models.map(model => model.id));
      },
      onOpen: () => store.set({ mode: openingMode(undefined) }),
      pivots: () => library.pivotMap(),
      measured: id => library.has(id),
      converting: (id, signal) => library.webStateChange(id, signal),
    });
    const share = new CatalogShare({ projectId, linkToken, token: webAuthToken, presence: shared.presence });
    activeLive.current = { session: shared, share, projectId, name };
    setLive({ session: shared, share });
    shared.start();
  }, [closeLive]);

  // An invitation opens the owner's project: signed in, in the app's live room with the room's rights; as a guest, view-only
  // from the catalog. Never from this browser's own copy or view of it. Its token stays out of the store's project id and of
  // any error text.
  const openInvite = useCallback(async (token: string) => {
    const ticket = ++request.current;
    const stale = () => ticket !== request.current;
    setInviteFailure(null);
    closeLive();
    startLoading('', t('#AppProjectFallbackName'), null, t('#CollabCheckingInvite'), 'none');
    let preview: Awaited<ReturnType<typeof fetchSharePreview>>;
    try {
      preview = await fetchSharePreview(token);
    } catch {
      if (stale()) return;
      store.set({ loading: false });
      setInviteFailure('unreachable');
      return;
    }
    if (stale()) return;
    if (!preview) {
      store.set({ loading: false });
      setInviteFailure('invalid');
      return;
    }
    const name = preview.title || t('#AppProjectFallbackName');
    store.set({ projectName: name, loadingStatus: null });
    if (webAuthToken()) openLive(preview.projectId, name, token);
    else await openGuest(preview.projectId, name, stale);
  }, [closeLive, openGuest, openLive]);

  // A sign-in that ended while in the room: the web forgets it and shows the guest's view of the same project.
  useEffect(() => {
    const target = activeLive.current;
    if (liveTerminal !== 'signed-out' || !target) return;
    signOut();
    closeLive();
    const ticket = ++request.current;
    startLoading('', target.name, null, null, 'none');
    void openGuest(target.projectId, target.name, () => ticket !== request.current);
  }, [liveTerminal, closeLive, openGuest]);

  useEffect(() => {
    if (!open || open.invite) return;
    if (open.collab) void openInvite(open.collab);
    else if (!(open.shared && openedShared.current === open.shared && store.get().projectId === open.id)) void load(open.id, open.name, open.shared);
  }, [open?.id, open?.invite, load, openInvite]);

  useEffect(() => () => {
    request.current += 1; activeSession.current?.close(); activeSession.current = null; openedShared.current = null;
    activeLive.current?.session.close(); activeLive.current?.share.dispose(); activeLive.current = null;
    transport.current?.dispose(); transport.current = null;
  }, []);

  useEffect(() => {
    const flush = () => { editor.current?.captureView(); store.saveLocal(); };
    window.addEventListener('pagehide', flush);
    return () => window.removeEventListener('pagehide', flush);
  }, []);

  useEffect(() => {
    if (!state.saveError) return;
    const protectUnsaved = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', protectUnsaved);
    return () => window.removeEventListener('beforeunload', protectUnsaved);
  }, [state.saveError]);

  const close = useCallback(() => navigate({ section: current.current.section, project: null }), [navigate]);
  // The project you left by «Покинуть проект»: its entry held the invitation, and Back would walk in through it again.
  const closeLeft = useCallback(() => navigate({ section: current.current.section, project: null }, false, true), [navigate]);

  // Project navigation for the agent tools: open a project by id, or start an empty one.
  useEffect(() => {
    setAppAccess({
      openProject: (id, name) => navigate({ section: current.current.section, project: projectDestination(id, name) }),
      createProject: name => {
        const project = { id: 'local-' + crypto.randomUUID(), name: name.trim() || t('#LibraryDefaultName'), savedAt: Date.now(), scene: createEmptyScene() };
        writeProject(project);
        navigate({ section: current.current.section, project: { id: project.id, name: project.name } });
        return { id: project.id, name: project.name };
      },
    });
    return () => setAppAccess(null);
  }, [navigate]);

  if (!open) {
    return <ProjectHome section={destination.section} onSection={section => navigate({ section, project: null })}
      onOpen={(id, name) => navigate({ section: destination.section, project: projectDestination(id, name) })}
      onCopyShared={async documentId => {
        const snapshot = await getTransport().read(documentId), id = 'local-' + crypto.randomUUID();
        writeProject({ id, scene: snapshot.scene, name: t('#LibraryCopyName', snapshot.name), savedAt: Date.now() });
        copyProjectView('shared-' + documentId, id);
      }}
      onShared={(documentId, invite) => navigate({ section: destination.section, project: { id: 'shared-' + documentId, name: t('#AppSharedProjectName'), shared: documentId, ...(invite ? { invite } : {}) } })}
      onInvite={token => navigate({ section: destination.section, project: inviteDestination(token) })} />;
  }

  if (open.shared && open.invite) return <JoinSharedProject key={open.shared + ':' + open.invite} documentId={open.shared} invite={open.invite}
    configured={collaborationConfigured} getTransport={getTransport} onJoined={enterShared} onClose={close} />;

  if (collaborationStatus === 'denied') return <main className="ws-project-loading collab-join"><section><h1>{t('#AppAccessClosed')}</h1><p>{t('#AppAskOwnerForLink')}</p><button className="ws-button primary" onClick={close}>{t('#AppBackToProjects')}</button></section></main>;

  const invite = open.collab;
  const failure = inviteFailure ?? (liveTerminal && liveTerminal !== 'signed-out' ? liveTerminal : null);

  if (!state.sceneOpen && state.error) {
    return (
      <div className="home">
        <header className="home-bar"><h1>{t('#AppOpenFailedTitle')}</h1></header>
        <p className="banner error">{state.error}</p>
        {open.shared && collaborationConfigured && <button className="seg" onClick={() => void load(open.id, open.name, open.shared)}>{t('#AppRetryConnection')}</button>}
        <button className="seg" onClick={close}>{t('#AppBackToProjects')}</button>
      </div>
    );
  }

  return <>
    <EditorView ref={editor} onClose={close} onLeftProject={closeLeft} onOpenProject={(id, name) => navigate({ section: current.current.section, project: projectDestination(id, name) })} collaboration={session} live={live?.session ?? null} liveShare={live?.share ?? null} sharedOpening={!!invite} />
    {invite && <SessionAlerts live={live?.session ?? null} failure={failure} onLeave={close} onRetryInvite={() => void openInvite(invite)} />}
  </>;
}

/** What a parse takes of the catalog: the models' tags, real sizes and pivots, once `ModelLibrary.prepare` fetched them. */
function catalogParseOptions(library: ModelLibrary, projectId: string) {
  return { projectId, modelTags: library.tagMap(), catalogDims: library.dimsMap(), catalogPivots: library.pivotMap() };
}

/** A shared `?project=` link names the ready-made projects; anything else is read from the scene. */
function knownProjectName(id: string): string {
  return demoProject(id)?.name ?? t('#AppProjectFallbackName');
}

/** A stand-in name the scene's own replaces: «Проект» in the interface language, or as stored before it was translated. */
/** The placeholder name, in whichever language it was saved. */
const isPlaceholderName = (name: string): boolean => isTextOf('#AppProjectFallbackName', name);

function roomTypeLabel(type: string): string {
  const labels: Record<string, TranslationKey> = {
    kitchens: '#RoomKitchen', kitchen: '#RoomKitchen', livingroom: '#RoomLiving', living: '#RoomLiving',
    diningroom: '#RoomDining', bedroom: '#RoomBedroom', bathroom: '#RoomBath', office: '#RoomHomeOffice',
  };
  const label = labels[type.toLowerCase()];
  return label ? t(label) : t('#AppProjectFallbackName');
}

/**
 * The loading shell of a project being opened. `view`, the one it was left on, sets the flags before the scene is
 * published, so the 3D view builds in the right mode and restores its own camera; `edit` `none` gives an invitation's words.
 */
function startLoading(projectId: string, projectName: string, view: ProjectView | null, loadingStatus: string | null = null, edit: EditScope = 'all'): void {
  store.set({ loading: true, loadingStatus, error: null, projectId, projectName, isDragging: false, edit,
    firstPerson: !!view?.firstPerson, showCeiling: !!view?.showCeiling, wallMode: view?.wallMode ?? 'full',
    windowView: view?.windowView ?? DEFAULT_WINDOW_VIEW, windowViewRotation: view?.windowViewRotation ?? 0, showWindowViews: false,
    tool: 'navigate', planSurface: view?.planSurface ?? 'floor', wallView: null, showLighting: false, showCatalog: false, showProperties: false, materialTarget: null, stats: {} });
}

/**
 * Whether asking again may open what failed: no answer at all (offline, a dropped connection, which `fetch` rejects
 * with a TypeError), a busy or failing catalog. A refused file or one that is not a project fails the same way again.
 */
function mayPassOnRetry(error: unknown): boolean {
  const status = (error as { status?: unknown } | null)?.status;
  return typeof status === 'number' ? status >= 500 || status === 408 || status === 429 : error instanceof TypeError;
}

/** The catalog project `id` fetched, its models asked of the queue, parsed and opened; nothing lands once `stale()`. */
async function loadCatalogProject(id: string, name: string, view: ProjectView | null, stale: () => boolean, options: { edit?: EditScope } = {}): Promise<void> {
  const library = new ModelLibrary();
  const progress = (done: number, total: number) => {
    if (!stale()) store.set({ loadingStatus: t('#AppLoadingModels', done, total) });
  };
  // The catalog manifest lands first: a model's tags decide how its item is PLACED
  // (a vertical LED strip laid flat on the ceiling), so the parse depends on it.
  const request = projectFileRequest(id);
  const [res] = await Promise.all([fetch(request.url, request.init), library.manifest()]);
  if (!res.ok) throw Object.assign(new Error(`${res.status} ${res.statusText}`), { status: res.status });
  const xml = await res.text();
  if (stale()) return;
  // A shared project names models the static catalog has never seen. They are converted
  // first, in parallel, because the parse needs each one's real size, pivot and tags —
  // the same reason the app downloads a shared project's assets before opening it.
  const draft = parseProject(xml, { projectId: id });
  store.set({ loadingStatus: t('#AppAskingCatalog') });
  // Asked before the scene is loaded: the queue converts a model outside the public catalog only for its project.
  setCatalogProject(id);
  const prepared = await library.prepare(draft.assets.models.map((m) => m.id), progress);
  if (stale()) return;
  // Models still in the catalog's queue arrive later; the project opens with placeholders.
  if (prepared.pending.length) store.set({ loadingStatus: t('#AppModelsPending', prepared.pending.length) });
  const scene = parseProject(xml, catalogParseOptions(library, id));
  const room = scene.levels[0]?.rooms[0];
  store.loadScene(scene, id, isPlaceholderName(name) ? (room?.name || roomTypeLabel(scene.roomType)) : name, view?.levelIndex ?? 0, options);
  store.set({ mode: openingMode(view?.mode) });
  store.saveLocal();
}

/** `collab` is an invitation's token, opened view-only; it lives in the address's fragment, which reaches no server log. */
interface Destination { section: HomeSection; project: { id: string; name: string; shared?: string; invite?: string; collab?: string } | null }
function projectDestination(id: string, name: string): NonNullable<Destination['project']> {
  const shared = sharedDocumentId(id);
  return shared ? { id: 'shared-' + shared, name, shared } : { id, name };
}
function inviteDestination(token: string): NonNullable<Destination['project']> {
  return { id: 'collab:' + token, name: t('#AppProjectFallbackName'), collab: token };
}
function readDestination(): Destination {
  const params = new URLSearchParams(location.search), fragment = new URLSearchParams(location.hash.slice(1));
  const rawSection = params.get('section');
  const section: HomeSection = rawSection === 'projects' || rawSection === 'gallery' || rawSection === 'feed' ? rawSection : 'home';
  const id = params.get('project');
  const shared = params.has('shared') ? params.get('shared') || 'invalid' : null, invite = fragment.get('invite'), collab = fragment.get('collab');
  return { section, project: shared ? { id: 'shared-' + shared, name: t('#AppSharedProjectName'), shared, ...(invite ? { invite } : {}) }
    : collab ? inviteDestination(collab) : id ? projectDestination(id, knownProjectName(id)) : null };
}
function destinationUrl(destination: Destination): URL {
  const url = new URL(location.href);
  url.searchParams.set('section', destination.section);
  url.searchParams.delete('project'); url.searchParams.delete('shared'); url.hash = '';
  if (destination.project?.shared) {
    url.searchParams.set('shared', destination.project.shared);
    if (destination.project.invite) url.hash = new URLSearchParams({ invite: destination.project.invite }).toString();
  } else if (destination.project?.collab) url.hash = new URLSearchParams({ collab: destination.project.collab }).toString();
  else if (destination.project) url.searchParams.set('project', destination.project.id);
  return url;
}
